import { useContext, useCallback, useState, useMemo, useRef } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import useToggle from 'hooks/useToggle'
import useCruiseSearchFacets from 'hooks/Cruise/useCruiseSearchFacets'
import { GlobalSearchStateContext, GlobalSearchDispatchContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import TextInputOnly from 'components/Common/Form/Input/TextInputOnly'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import IconButton from 'LuxKit/Button/IconButton'
import CruiseLocationContent from './CruiseLocationContent'
import CruiseFilterFooter from '../Common/CruiseFilterFooter'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Caption from 'LuxKit/Typography/Caption'
import CruiseFilterInput from 'components/Cruises/SearchPage/Filters/Inputs/Common/CruiseFilterInput'
import useGlobalSearchTypeahead from 'hooks/GlobalSearch/useGlobalSearchTypeahead'
import { TOUR_V2_LOCATION_SEARCH_TYPEAHEAD_TYPES, TOUR_V2_SEARCH_TYPES } from 'constants/tours'
import * as Analytics from 'analytics/analytics'
import { mapCruiseLineFilterToSnowplowSearchEvent } from 'analytics/mapSnowplowSearchTracking'
import { searchEventWithContext } from 'analytics/snowplow/events'

const SET_SEARCH_ITEMS = GlobalSearchStateActions.SET_SEARCH_ITEMS

interface Props {
  isChipMode?: boolean
  initialFilters?: App.CruiseInitialFilters
  hideTrendOptions?: boolean
  hideImages?: boolean
  shouldIgnoreFlashOffers?: boolean
  onChange: (values: App.CruiseGlobalFilters) => void
}

function CruiseLocationMobileInput({ onChange, isChipMode, initialFilters, hideTrendOptions, hideImages, shouldIgnoreFlashOffers }: Props) {
  const intl = useIntl()
  const inputTriggerRef = useRef<HTMLButtonElement>(null)
  const inputSearchRef = useRef<HTMLInputElement>(null)

  const globalSearchState = useContext(GlobalSearchStateContext)
  const { searchItems, suggestedSearchItems } = globalSearchState
  const searchDispatch = useContext(GlobalSearchDispatchContext)

  const [searchTerm, setSearchTerm] = useState('')
  const [isAllSelected, setIsAllSelected] = useState(false)
  const [isOpen, toggle,, close] = useToggle(false)

  const [facets] = useCruiseSearchFacets({ facetTypes: ['destination_ports'], ...(initialFilters || {}) })
  useGlobalSearchTypeahead({
    search: searchTerm,
    searchTypes: TOUR_V2_SEARCH_TYPES,
    typeaheadTypes: TOUR_V2_LOCATION_SEARCH_TYPEAHEAD_TYPES,
  })

  const locations = useMemo(() => {
    if (!searchTerm) {
      return hideTrendOptions ? facets : facets.filter(({ isTrend }) => isTrend)
    }

    let filtered = facets.filter(({ name }) => name.toLowerCase().includes(searchTerm.toLowerCase()))
    if (!filtered.length) {
      filtered = facets.filter(({ name }) => {
        return suggestedSearchItems?.find((suggestedValue) => {
          return name.includes(suggestedValue?.format?.secondaryText!)
        })
      })
    }
    return filtered.slice(0, 100)
  }, [searchTerm, facets, suggestedSearchItems, hideTrendOptions])

  const inputValue = useMemo(() => {
    if (isOpen) return searchTerm
    if (searchItems.length > 1) {
      const names = searchItems.slice(0, 1).map((item) => item.format.mainText).join(', ')
      return `${names} + ${searchItems.length - 1}`
    }
    return searchItems.map((item) => item.format.mainText).join(', ')
  }, [searchItems, isOpen, searchTerm])

  const handleOnApply = useCallback(() => {
    Analytics.trackClientEvent({
      subject: 'filter_applied',
      action: 'applied',
      category: 'cruise_location_filter',
      type: 'interaction',
    })

    Analytics.trackEvent(searchEventWithContext({
      searchEvent: { type: 'filters' },
      sortContext: { value: 'recommended' },
      searchContext: mapCruiseLineFilterToSnowplowSearchEvent(globalSearchState),
      filterContexts: searchItems.map(item => ({
        label: 'Destination',
        type: 'destination',
        value: item.value,
      })),
    }))

    onChange({ searchItems })
    close()
  }, [onChange, searchItems, close, globalSearchState])

  const handleOnClear = useCallback(() => {
    Analytics.trackClientEvent({
      subject: 'filter_cleared',
      action: 'cleared',
      category: 'cruise_location_filter',
      type: 'interaction',
    })
    searchDispatch({ type: SET_SEARCH_ITEMS, searchItems: [] })
    setSearchTerm('')
    setIsAllSelected(false)
  }, [searchDispatch])

  const handleOnChange = useCallback((searchItems: Array<App.SearchItem>) => {
    Analytics.trackClientEvent({
      subject: 'filter_item_selected',
      action: 'selected',
      category: 'cruise_location_filter',
      type: 'interaction',
    })
    searchDispatch({ type: SET_SEARCH_ITEMS, searchItems })
    setIsAllSelected(false)
  }, [searchDispatch])

  const handleSelectAll = useCallback(() => {
    Analytics.trackClientEvent({
      subject: isAllSelected ? 'filter_all_unselected' : 'filter_all_selected',
      action: isAllSelected ? 'unselected' : 'selected',
      category: 'cruise_location_filter',
      type: 'interaction',
    })
    searchDispatch({ type: SET_SEARCH_ITEMS, searchItems: [] })
    setIsAllSelected(!isAllSelected)
  }, [searchDispatch, isAllSelected])

  const handlerSearchTerm = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }, [])

  const handlerClearSearchTerm = useCallback(() => setSearchTerm(''), [])

  const cruisingToLabel = intl.formatMessage({
    id: 'tBtn2L0IlppgYQi111yI',
    defaultMessage: 'Cruising to',
    description: 'Label for cruise destination filter in the cruise offer or booking section',
  })

  const allDestinationsPlaceholder = intl.formatMessage({
    id: 'mgP9opd4uzBWjqTTR3R3',
    defaultMessage: 'All destinations',
    description: 'Placeholder text when all destinations are selected',
  })

  const searchDestinationsPlaceholder = intl.formatMessage({
    id: 'jP1LkwiTaM5uXrUNqcSp',
    defaultMessage: 'Search destinations',
    description: 'Placeholder text for destination search input on the form',
  })

  const searchResultsCaption = useMemo(() => {
    if (searchTerm) {
      return intl.formatMessage(
        {
          id: 'RQB1-Ryuo_-kWNd7TRex',
          defaultMessage: 'Search results for "{searchTerm}"',
          description: 'Caption showing search results for a specific search term',
        },
        { searchTerm },
      )
    }
    return intl.formatMessage({
      id: 'yZmprL_5rqqk-raQwVRU',
      defaultMessage: 'or browse by popular regions',
      description: 'Descriptive caption text suggesting browsing popular regions',
    })
  }, [intl, searchTerm])

  return <>
    <CruiseFilterInput
      isOpen={isOpen}
      label={cruisingToLabel}
      placeholder={allDestinationsPlaceholder}
      inputValue={inputValue}
      formFieldRef={inputTriggerRef}
      selectedItems={searchItems.length}
      onClick={() => {
        Analytics.trackClientEvent({
          subject: 'filter_opened',
          action: 'opened',
          category: 'cruise_location_filter',
          type: 'interaction',
        })
        toggle()
      }}
      isChipMode={isChipMode}
    />

    <ModalBase
      mode="drawer"
      isOpen={isOpen}
      onClose={toggle}
    >
      <ModalHeader
        title={cruisingToLabel}
        onCloseButtonClick={toggle}
      />
      <ModalBody>
        <ModalContent flushListItems>
          <VerticalSpacer gap={12}>
            <TextInputOnly
              ref={inputSearchRef}
              noValidationSpacing
              placeholder={searchDestinationsPlaceholder}
              defaultValue={inputValue}
              onChange={handlerSearchTerm}
              endIcon={searchTerm.length > 0 ?
                  <IconButton kind="tertiary" onClick={handlerClearSearchTerm}><LineTimesIcon /></IconButton> :
                  <LineSearchIcon />}
            />

            <Caption variant="large" weight="bold">
              {searchResultsCaption}
            </Caption>

            <CruiseLocationContent
              locations={locations}
              searchTerm={searchTerm}
              searchItems={searchItems}
              onChange={handleOnChange}
              onSelectAll={handleSelectAll}
              inputSearchRef={inputSearchRef}
              isAllSelected={isAllSelected}
              hideImages={hideImages}
              initialFilters={initialFilters}
            />
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
      <ModalFooter>
        <CruiseFilterFooter
          onClear={handleOnClear}
          onApply={handleOnApply}
          initialFilters={initialFilters}
          shouldIgnoreFlashOffers={shouldIgnoreFlashOffers}
        />
      </ModalFooter>
    </ModalBase>
  </>
}

export default CruiseLocationMobileInput
